import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { AsyncPipe, DatePipe } from '@angular/common';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxDropdownModule } from '@aposin/ng-aquila/dropdown';
import { NxFormfieldModule } from '@aposin/ng-aquila/formfield';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { NxInputModule } from '@aposin/ng-aquila/input';
import { NxMessageModule, NxMessageToastService } from '@aposin/ng-aquila/message';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { NxTaglistModule } from '@aposin/ng-aquila/taglist';
import { catchError, take, throwError } from 'rxjs';
import { HttpError } from '../../../models/http-error.model';
import { ServiceConfig } from '../../../models/service-config.model';
import { MockDataService } from '../../../services/mock-data.service';
import { PermissionService } from '../../../services/permission.service';
import { ServiceConfigService } from '../../../services/service-config.service';
import { isLocalEnvironment } from '../../../utils/environment.utils';

@Component({
  selector: 'app-service-config-form',
  standalone: true,
  imports: [
    NxFormfieldModule,
    NxTaglistModule,
    NxDropdownModule,
    NxIconModule,
    NxInputModule,
    NxButtonModule,
    ReactiveFormsModule,
    NxSpinnerModule,
    NxMessageModule,
    AsyncPipe,
    DatePipe,
  ],
  templateUrl: './service-config-form.component.html',
  styleUrl: './service-config-form.component.scss'
})
export class ServiceConfigFormComponent implements OnChanges, OnInit {
  private serviceConfigService = inject(ServiceConfigService);
  private mockDataService = inject(MockDataService);
  private formBuilder = inject(FormBuilder);
  private messageToastService = inject(NxMessageToastService);
  permissionService = inject(PermissionService);

  @Input() serviceConfigId: string | null = null;
  @Output() closePanel = new EventEmitter<{ refreshList: boolean; updatedServiceConfig?: ServiceConfig }>();

  isServiceConfigLoading = false;
  isServiceConfigUpdating = false;
  serviceConfigForm: FormGroup | null = null;
  emailDistributionList: string[] = [];
  responseError: HttpError | null = null;

  ngOnChanges(): void {
    this.initData();
  }

  ngOnInit(): void {
    this.initForm();
  }

  initForm() {
    this.serviceConfigForm = this.formBuilder.group({
      pagerDutyServiceId: [null, Validators.required],
      pagerDutyServiceName: [null, Validators.required],
      serviceIntegrationManagementServiceOffering: [null, Validators.required],
      serviceIntegrationManagementBusinessService: [null, Validators.required],
      serviceIntegrationManagementAssignmentGroup: [null, Validators.required],
      emailDistributionList: null,
      contactEmail: [null, [Validators.required, Validators.email]],
      jiraProjectId: [null, Validators.required],
      pagerDutyTeamId: [null, Validators.required],
      collaborativePlatformChannelId: [null],
      collaborativePlatformGroupId: [null],
      creationDateTime: [{ value: null, disabled: true }],
      createdby: [{ value: null, disabled: true }],
      lastUpdateDateTime: [{ value: null, disabled: true }],
      lastUpdatedby: [{ value: null, disabled: true }],
    });
  }

  addEmailToDistributionList(value: string, inputElement: HTMLInputElement) {
    const trimmedEmail = value.trim();

    if (!trimmedEmail) {
      return;
    }

    if (!this.isValidEmail(trimmedEmail)) {
      this.messageToastService.open(`Invalid email format: ${trimmedEmail}`, {
        context: 'info',
        duration: 3000,
      });
      return;
    }

    if (this.emailDistributionList.includes(trimmedEmail)) {
      this.messageToastService.open(`Email already exists: ${trimmedEmail}`, {
        context: 'info',
        duration: 3000,
      });
      inputElement.value = '';
      return;
    }

    this.emailDistributionList = [...this.emailDistributionList, trimmedEmail];
    inputElement.value = '';
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  validateAllEmails(): boolean {
    const contactEmail = this.serviceConfigForm?.get('contactEmail')?.value;
    if (contactEmail && !this.isValidEmail(contactEmail)) {
      this.messageToastService.open('Invalid contact email format', {
        context: 'info',
        duration: 5000,
      });
      return false;
    }

    for (const email of this.emailDistributionList) {
      if (!this.isValidEmail(email)) {
        this.messageToastService.open(`Invalid email in distributions: ${email}`, {
          context: 'info',
          duration: 5000,
        });
        return false;
      }
    }

    return true;
  }

  initData() {
    if (!this.serviceConfigId) {
      return;
    }

    this.responseError = null;
    this.emailDistributionList = [];
    this.isServiceConfigLoading = true;

    const dataService = isLocalEnvironment()
      ? this.mockDataService.getServiceConfig(this.serviceConfigId)
      : this.serviceConfigService.getServiceConfig(this.serviceConfigId);

    dataService
      .pipe(
        take(1),
        catchError((error) => this.handleError('Failed to load service config', 'loadServiceConfig', error)),
      )
      .subscribe((response) => {
        const serviceConfig = response?.body || null;
        this.isServiceConfigLoading = false;

        this.emailDistributionList = serviceConfig?.emailDistributionList ?? [];
        this.serviceConfigForm?.patchValue({
          ...serviceConfig
        })
      });
  }

  updateServiceConfig(inputElement?: HTMLInputElement) {
    if (!this.serviceConfigId || !this.serviceConfigForm || !this.serviceConfigForm.valid) {
      return;
    }

    if (inputElement && inputElement.value.trim()) {
      const trimmedEmail = inputElement.value.trim();

      if (this.isValidEmail(trimmedEmail)) {
        if (!this.emailDistributionList.includes(trimmedEmail)) {
          this.emailDistributionList = [...this.emailDistributionList, trimmedEmail];
        }
      } else {
        this.messageToastService.open(`Invalid email format: ${trimmedEmail}`, {
          context: 'info',
          duration: 3000,
        });
      }

      inputElement.value = '';
    }

    if (!this.validateAllEmails()) {
      return;
    }

    this.isServiceConfigUpdating = true;
    this.responseError = null;
    const serviceConfig = {
      ...this.serviceConfigForm.value,
      emailDistributionList: this.emailDistributionList
    };

    delete serviceConfig.pagerDutyServiceId;
    delete serviceConfig.pagerDutyServiceName;

    const dataService = isLocalEnvironment()
      ? this.mockDataService.patchServiceConfig(this.serviceConfigId, serviceConfig)
      : this.serviceConfigService.patchServiceConfig(this.serviceConfigId, serviceConfig);

    dataService
      .pipe(
        take(1),
        catchError((error) => this.handleError('Failed to update service config', 'patchServiceConfig', error)),
      )
      .subscribe((response): void => {
        this.isServiceConfigUpdating = false;
        this.messageToastService.open('service config updated successfully', {
          context: 'success',
          duration: 5000,
        });
        this.onCloseForm(true, response?.body || undefined);
      });
  }

  onCloseForm(refreshList = false, updatedServiceConfig?: ServiceConfig) {
    this.serviceConfigForm?.reset();
    this.closePanel.emit({ refreshList, updatedServiceConfig });
  }

  handleError(error: string, errorType: string, errorDetails: HttpErrorResponse) {
    this.isServiceConfigLoading = false;
    this.isServiceConfigUpdating = false;

    this.responseError = {
      error,
      errorType,
      details: null
    }

    if (errorDetails?.error?.message) {
      this.responseError.details = errorDetails.error.message;
    }

    return throwError(() => error);
  }
}
